import { mapDesigns } from "@mono/mapDesigns"
import ActionCard from "@/Components/ActionCard"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData, setBoardPickerMode } from "@/store/globalSlice"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { MainButton } from "@/Components/MainButton"
import Color from "color"
import { socket } from "@/utils/utils"
import { useState } from "react"
import PlayerCardsPlayerItem from "@/Modals/PlayerCardsPlayerItem"
import classNames from "classnames"
import ModalButton from "@/Components/ModalButton"
import {
  allRoles,
  getHouseTaxPerHouse,
  getPropertyTaxPerProperty,
  maxCardsPerTurn,
} from "@mono/constants"
import Money from "@/Components/Money"
import { actionCardTitles } from "@/utils/actionCardTitles"
import {
  getBoardPickerEligibleOrders,
  getBoardPickerTargetKind,
  isBoardPickerCardId,
} from "@/utils/boardPickerHelpers"

// One row of the "what happens next" list shown for a board-targeted card.
// Three states, because the row's whole job is to place the player inside a
// sequence: what is already done, the one thing they have to do now, and what
// that leads to. The current step is the loud one — it is the answer to "so
// why is this panel not asking me to pick anything?".
const PickerStep: React.FC<{
  state: "done" | "current" | "next"
  icon: string
  title: React.ReactNode
  detail: string
}> = ({ state, icon, title, detail }) => (
  <li
    className={classNames(
      "flex items-start gap-3 rounded-lg border px-3 py-2",
      {
        "border-green-400/30 bg-green-500/10": state === "done",
        "border-cyan-300/40 bg-cyan-400/10": state === "current",
        "border-white/10 bg-white/5": state === "next",
      }
    )}
  >
    <i
      className={classNames("fa-solid mt-0.5 w-4 shrink-0 text-center", icon, {
        "text-green-400": state === "done",
        "text-cyan-300": state === "current",
        "text-white/40": state === "next",
      })}
    ></i>
    <div className="flex flex-col gap-0.5 min-w-0">
      <span
        className={classNames(
          "font-poppins text-sm leading-snug",
          state === "next" ? "text-white/70" : "text-white"
        )}
      >
        {title}
      </span>
      <span className="font-poppins text-xs leading-snug text-white/50">
        {detail}
      </span>
    </div>
  </li>
)

// Perimeter tiles of the thumbnail board below that stand in for the ones that
// will glow for real. Spread around all four sides so the picture reads as
// "several places on the board", not one corner.
const previewLitCells = [2, 9, 15, 23]

const PlayerCardsModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const modalData = useAppSelector((state) => state.global.modalData)
  const playerCardsModal = modalData.playerCards
  const map = mapDesigns.find((map) => map.id === myGame?.settings.mapId)
  const player = myGame?.situation?.players.find(
    (p) => p.order === playerCardsModal?.playerOrder
  )

  const [selectedCard, setSelectedCard] = useState<IPlayerActionCard | null>(
    null
  )
  const [selectedPlayerOrder, setSelectedPlayerOrder] =
    useState<PlayerOrder | null>(null)
  const [selectedRole, setSelectedRole] = useState<GameRoleId | null>(null)

  if (!playerCardsModal?.visible || !player) return null
  const handleClose = () => {
    dispatch(
      setModalData({
        playerCards: { visible: false, playerOrder: player.order },
      })
    )
    setSelectedCard(null)
    setSelectedPlayerOrder(null)
  }

  const handleCardClick = (card: IPlayerActionCard) => {
    setSelectedCard(card)
    if (
      card.cardId !== "perform-tax-audit" &&
      card.cardId !== "forced-auction" &&
      card.cardId !== "get-200-from-player" &&
      card.cardId !== "get-10-percent-from-player" &&
      card.cardId !== "apply-property-tax" &&
      card.cardId !== "apply-house-tax"
    ) {
      setSelectedPlayerOrder(null)
    }
  }

  const handlePlayerSelect = (playerOrder: PlayerOrder) => {
    setSelectedPlayerOrder(playerOrder)
  }

  const handlePlayCard = () => {
    if (!selectedCard || !myGame?.id) return
    if (selectedCard.cardId === "change-role-for-three-turns" && selectedRole === null) return
    if (
      selectedCard.cardId === "apply-property-tax" &&
      selectedPlayerOrder === null
    )
      return
    socket.emit("game.playActionCard", {
      roomid: myGame.id,
      cardId: selectedCard.cardId,
      playerOrder: player.order,
      data: {
        targetedPlayerOrder: selectedPlayerOrder as PlayerOrder,
        changeRole: selectedRole || undefined,
      },
    })
    handleClose()
  }

  // The board-targeted cards no longer pick their target in this modal —
  // closing it and entering board-picker mode is the "play" action for them;
  // the actual card play fires once a cell is clicked on the board (see
  // BoardCellWrapper). That includes the swap card, whose target is a player:
  // the player is on the board too, so pointing at them there beats hunting
  // for their row in a list.
  const handleChooseFromBoard = () => {
    if (!selectedCard || !myGame?.id) return
    if (!isBoardPickerCardId(selectedCard.cardId)) return
    dispatch(
      setBoardPickerMode({
        cardId: selectedCard.cardId,
        cardInstanceId: selectedCard.id,
        playerOrder: player.order,
      })
    )
    handleClose()
  }

  const otherPlayers =
    myGame?.situation?.players.filter(
      (p) => p.order !== player.order && !p.isBankrupt
    ) || []

  // The two tax cards charge a flat rate against what the target owns, and the
  // property rate even changes with the map — without spelling it out the
  // player only learns the damage after the card is already spent.
  const taxRate: { unit: "property" | "house"; rate: number } | null =
    selectedCard?.cardId === "apply-property-tax"
      ? {
          unit: "property",
          rate: getPropertyTaxPerProperty(myGame?.settings.mapId || ""),
        }
      : selectedCard?.cardId === "apply-house-tax"
        ? {
            unit: "house",
            rate: getHouseTaxPerHouse(myGame?.settings.mapId || ""),
          }
        : null

  // Both pickers below serve several cards each, and the left grid can hold two
  // copies of the same card, so the panel names the card it is about to spend.
  // Falls back to the card's face text the same way the game log does.
  const selectedCardTitle = selectedCard
    ? actionCardTitles[selectedCard.cardId] ||
      map?.allChances.find((c) => c.id === selectedCard.cardId)?.text
    : null

  const boardPickerCardId: BoardPickerCardId | null =
    selectedCard && isBoardPickerCardId(selectedCard.cardId)
      ? selectedCard.cardId
      : null
  // What that card will be picking out on the board, which is all that differs
  // between the four property cards and the swap card from here on.
  const boardPickerTargetKind = boardPickerCardId
    ? getBoardPickerTargetKind(boardPickerCardId)
    : null
  // Named once and used both on the button and in the step that tells the
  // player to press it — quoting a label that has drifted out of sync with the
  // button would be worse than not quoting it at all.
  const proceedLabel =
    boardPickerTargetKind === "player"
      ? "Proceed to choose player from board"
      : "Proceed to choose property from board"

  // How many targets are actually out there, so the promised next step is a
  // countable thing rather than a vague one — and so a card with nothing to hit
  // says so here instead of after the modal has already closed.
  const boardPickerEligibleCount =
    boardPickerCardId && myGame
      ? getBoardPickerEligibleOrders(myGame, boardPickerCardId, player.order)
          .length
      : 0

  // Every reason the selected card cannot be played right now, lifted out of
  // the button so the panel can also name the reason: a button that greys out
  // without explanation is exactly the kind of dead end this panel exists to
  // prevent.
  const selectedCardDisabled =
    !!selectedCard &&
    ((selectedCard.cardId === "change-role-for-three-turns" &&
      selectedRole === null) ||
      player.cardsPlayed === maxCardsPerTurn ||
      (player.position === 0 && selectedCard.cardId === "advance-to-go") ||
      player.id !== myGame?.situation?.turn ||
      selectedCard.cardId === "forced-buy" ||
      selectedCard.cardId === "say-no" ||
      selectedCard.cardId === "dobule-rent" ||
      ((selectedCard.cardId === "perform-tax-audit" ||
        selectedCard.cardId === "get-200-from-player" ||
        selectedCard.cardId === "get-10-percent-from-player" ||
        selectedCard.cardId === "apply-property-tax" ||
        selectedCard.cardId === "apply-house-tax") &&
        selectedPlayerOrder === null))

  const boardPickerBlockedReason =
    player.id !== myGame?.situation?.turn
      ? "The button below stays disabled until it is your turn."
      : player.cardsPlayed === maxCardsPerTurn
        ? "You have already played your card for this turn."
        : null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-xl border-2 border-gray-700 z-[200] flex flex-col">
      <div className="flex justify-between items-center p-4 md:p-8 border-b border-gray-700">
        <div className="flex flex-col">
          <h2 className="text-2xl md:text-2xl font-poppins text-white">
            Player Cards
          </h2>
        </div>
        <ModalCloseButton
          onClick={handleClose}
          data-testid="game.player-cards-modal-close"
        />
      </div>

      <div className="flex-1 min-h-0">
        <div className="h-full grid grid-cols-[minmax(0,1fr)_30rem]">
          <div className="overflow-y-auto p-4 md:p-8">
            <div className="flex flex-wrap gap-4 md:gap-4 cursor-pointer">
              {player.cards.map((card) => {
                const chanceCard = map?.allChances.find(
                  (c) => c.id === card.cardId
                )
                if (!chanceCard) return null

                return (
                  <div
                    key={card.id}
                    className={classNames(
                      "flex justify-center transition-transform duration-150",
                      selectedCard?.id === card.id && "scale-105"
                    )}
                    onClick={() => handleCardClick(card)}
                  >
                    <ActionCard
                      card={chanceCard}
                      className={classNames("transition-shadow duration-150", {
                        // Two rings and a wide glow: the thin dark gap keeps the
                        // green from bleeding into the card's beige face, so the
                        // selection still reads from across the grid.
                        "ring-4 ring-green-400 ring-offset-2 ring-offset-gray-900 shadow-[0_0_30px_10px_rgba(34,197,94,0.55)]":
                          selectedCard?.id === card.id,
                      })}
                    />
                  </div>
                )
              })}
            </div>
          </div>

          <div className="overflow-y-auto w-full flex flex-col gap-4 p-4 md:p-8 border-l border-gray-700">
            {(selectedCard?.cardId === "perform-tax-audit" ||
              selectedCard?.cardId === "get-200-from-player" ||
              selectedCard?.cardId === "apply-property-tax" ||
              selectedCard?.cardId === "apply-house-tax" ||
              selectedCard?.cardId === "get-10-percent-from-player") && (
                <div className="bg-gray-800 p-4 rounded-lg w-full">
                  {/* The rate is one fact that applies to the whole list, so it
                      rides next to the heading instead of repeating itself on
                      every row — the rows only carry what differs. */}
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      {selectedCardTitle && (
                        <span className="font-poppins text-xs uppercase tracking-wide text-white/40 truncate">
                          {selectedCardTitle}
                        </span>
                      )}
                      <h3 className="text-white text-lg md:text-lg font-poppins">
                        Select Player
                      </h3>
                    </div>
                    {taxRate && (
                      <div
                        className="flex items-center gap-1.5 shrink-0 rounded-md border border-amber-400/20 bg-amber-500/10 px-2 py-1"
                        data-testid="game.player-cards-tax-rate"
                      >
                        <i
                          className={classNames(
                            "fa-solid text-xs text-amber-300/70",
                            taxRate.unit === "house"
                              ? "fa-house"
                              : "fa-building"
                          )}
                        ></i>
                        <Money
                          money={taxRate.rate}
                          className="text-amber-300 text-sm font-semibold font-poppins leading-none"
                        />
                        <span className="text-amber-300/70 font-poppins text-xs leading-none whitespace-nowrap">
                          per {taxRate.unit}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 w-full">
                    {otherPlayers.map((otherPlayer) => {
                      const otherUser = myGame?.players.find(
                        (p) => p.email === otherPlayer.id
                      )
                      if (!otherUser) return null

                      return (
                        <PlayerCardsPlayerItem
                          key={otherPlayer.order}
                          player={otherPlayer}
                          user={otherUser}
                          selected={selectedPlayerOrder === otherPlayer.order}
                          onClick={() => handlePlayerSelect(otherPlayer.order)}
                          tax={taxRate || undefined}
                        />
                      )
                    })}
                  </div>
                </div>
              )}

            {selectedCard?.cardId === "change-role-for-three-turns" && (
              <div className="bg-gray-800 p-4 rounded-lg w-full">
                <h3 className="text-white text-lg md:text-lg font-poppins mb-4">
                  Select Role
                </h3>
                <div className="space-y-2 w-full">
                  {allRoles.map((role) => {

                    return (
                      <div
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={classNames("cursor-pointer w-full", {
                          "ring-4 ring-green-500":
                            selectedRole === role
                        })}
                      >
                        <div className="text-white text-lg md:text-lg font-poppins">
                          {role}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>)}
            {/* These cards have no target to pick in here, and an empty column
                where every other card shows a list reads as "something is
                broken" or "I have already done this". So the column spells the
                sequence out instead: what is done, that pressing the button
                below is the next move, and that the card is not spent until a
                tile is clicked out on the board. */}
            {boardPickerTargetKind && (
              <div
                data-testid="game.player-cards-board-picker-steps"
                className="bg-gray-800 p-4 rounded-lg w-full flex flex-col gap-4"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  {selectedCardTitle && (
                    <span className="font-poppins text-xs uppercase tracking-wide text-white/40 truncate">
                      {selectedCardTitle}
                    </span>
                  )}
                  <h3 className="text-white text-lg md:text-lg font-poppins">
                    {boardPickerTargetKind === "player"
                      ? "This card picks its player on the board"
                      : "This card picks its property on the board"}
                  </h3>
                </div>

                <div className="flex items-center gap-4">
                  {/* A thumbnail of where this is heading: the board with a few
                      tiles lit and the card's crosshair sitting in the middle.
                      Same arrangement the real picker produces, so the promised
                      step is recognisable the moment it arrives. */}
                  <div className="relative h-24 w-24 shrink-0 rounded-md border border-white/10 bg-black/30 p-1.5 sm:h-20 sm:w-20">
                    <div className="grid h-full w-full grid-cols-5 grid-rows-5 gap-[3px]">
                      {Array.from({ length: 25 }, (_, i) => {
                        const row = Math.floor(i / 5)
                        const col = i % 5
                        if (row > 0 && row < 4 && col > 0 && col < 4)
                          return <span key={i} />
                        return (
                          <span
                            key={i}
                            className={classNames(
                              "rounded-[2px]",
                              previewLitCells.includes(i)
                                ? "bg-cyan-400/80 animate-pulse"
                                : "bg-white/10"
                            )}
                          />
                        )
                      })}
                    </div>
                    <i
                      className={classNames(
                        "fa-solid absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-cyan-300",
                        boardPickerTargetKind === "player"
                          ? "fa-user-group"
                          : "fa-location-crosshairs"
                      )}
                    ></i>
                  </div>
                  <p className="text-white/60 text-sm font-poppins leading-snug">
                    There is nothing left to choose in this window —{" "}
                    {boardPickerTargetKind === "player"
                      ? "the player to swap with"
                      : "the target property"}{" "}
                    is picked out on the board itself.
                  </p>
                </div>

                <ol className="flex flex-col gap-2">
                  <PickerStep
                    state="done"
                    icon="fa-circle-check"
                    title="Card selected"
                    detail={selectedCardTitle || "Ready to play"}
                  />
                  <PickerStep
                    state="current"
                    icon="fa-map-location-dot"
                    title={
                      <>
                        Press <span className="font-semibold text-cyan-200">“{proceedLabel}”</span> below
                      </>
                    }
                    detail="This window closes and the board takes over."
                  />
                  <PickerStep
                    state="next"
                    icon="fa-hand-pointer"
                    title={
                      boardPickerTargetKind === "player"
                        ? "Tap the tile your target is standing on"
                        : "Tap one of the highlighted properties"
                    }
                    detail="The card is only spent then — you can still cancel before that."
                  />
                </ol>

                {/* The two things that turn the sequence above into a dead end:
                    a card with no legal target, and a button that will be greyed
                    out when the player reaches for it. */}
                {(boardPickerEligibleCount === 0 || boardPickerBlockedReason) && (
                  <p
                    data-testid="game.player-cards-board-picker-warning"
                    className="flex items-start gap-2 rounded-md border border-amber-400/20 bg-amber-500/10 px-3 py-2 font-poppins text-xs leading-snug text-amber-200/90"
                  >
                    <i className="fa-solid fa-triangle-exclamation mt-0.5"></i>
                    {boardPickerEligibleCount === 0
                      ? "Nothing on the board can take this card right now."
                      : boardPickerBlockedReason}
                  </p>
                )}
                {boardPickerEligibleCount > 0 && !boardPickerBlockedReason && (
                  <p className="flex items-center gap-2 font-poppins text-xs text-cyan-100/70">
                    <i className="fa-solid fa-lightbulb"></i>
                    {boardPickerEligibleCount}{" "}
                    {boardPickerTargetKind === "player"
                      ? boardPickerEligibleCount === 1
                        ? "tile"
                        : "tiles"
                      : boardPickerEligibleCount === 1
                        ? "property"
                        : "properties"}{" "}
                    will light up on the board.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedCard && (
        <div className="p-4 md:p-8 border-t border-gray-700">
          <ModalButton
            color="green"
            onClick={boardPickerCardId ? handleChooseFromBoard : handlePlayCard}
            className="w-full md:w-auto min-w-[8rem] md:min-w-[10rem]"
            disabled={selectedCardDisabled}
          >
            <div className="flex items-center gap-2">
              <i
                className={classNames(
                  "fa-solid text-white fa-xl",
                  boardPickerCardId ? "fa-map-location-dot" : "fa-play"
                )}
              ></i>
              <div className="text-white font-medium font-poppins text-xl">
                {boardPickerCardId ? proceedLabel : "Play Card"}
              </div>
            </div>
          </ModalButton>
        </div>
      )}
    </div>
  )
}

export default PlayerCardsModal
